import type { AiIdentification, AiIdentificationSummary } from "@/services/aiIdentification/aiIdentificationTypes";
import { CategoryId } from "@/services/collection/categories/categoriesSchema";
import type { CollectionItem } from "@/services/collection/item";
import { createCollectionItem, updateCollectionItem } from "@/services/collection/item";
import { setImagesFromAiIdentification } from "@/services/collection/item/gallery";
import * as PostHog from "@/services/analytics/postHog";
import { factoryImage, type Image } from "@/services/images";
import type { PriceNumeric } from "@/services/prices/priceTypes";
import { isNumericPrice } from "@/services/prices/priceValidators";

export const createCollectionItemFromIdentification = async (identification: AiIdentification): Promise<CollectionItem> => {
  const collectible = identification.collectible;
  if (collectible === null || collectible === undefined) {
    throw new Error("It should not be possible to create collection item from old ai identification without collectible.");
  }

  // 1) Create item itself
  const item = await createCollectionItem({
    categoryId: collectible.categoryId,
    collectibleId: collectible.id,
    set: collectible.id,
    minifigure: collectible.id,
    userCollectionListName: "",
  });

  const promises = [];

  // 2) Add images
  promises.push(setImagesFromAiIdentification(item, identification));

  // 3) Set identification origin
  const aiIdentificationSourceId = identification.id;
  promises.push(updateCollectionItem(item.id, { aiIdentificationSourceId }));

  // 4) Save AI-generated description
  const publicNote = identification.description ?? "";
  promises.push(updateCollectionItem(item.id, { publicNote }));

  await Promise.all(promises);
  PostHog.trackEvent(PostHog.EventName.AiIdentification_ItemCreated);
  return item;
};

export const SupportedCategoryIds = [CategoryId.SportsCards, CategoryId.Pokemon];

export const getImages = (item: AiIdentificationSummary | AiIdentification): Image[] => item.images.map(({ imageUrl }) => factoryImage(0 /* fake ID */, imageUrl, item.name));

export const hasPrice = (item: AiIdentificationSummary | AiIdentification): item is (AiIdentification | AiIdentification) & { priceFrom: PriceNumeric; priceTo: PriceNumeric } => isNumericPrice(item.priceFrom) && isNumericPrice(item.priceTo);
